<!-- AppLayout.vue -->
<template>
  <div v-cloak class="page">
    <!-- …NavBar etc… -->
   <NavBar ref="primaryRef" />

    <transition-group
      v-if="hasNotifications && isCr59Enabled" class="banner-wrapper pinned"
      name="slide-fade"
      tag="div"
    >
        <HandoverNotificationBanner
            v-for="(id, index) in handoverBanners"
            :key="id"
            ref="handoverNotificationBannerRef"
            pinned="false"
            @view-details="handleViewDetails()"
          />
    </transition-group>

    <!-- Main content container -->
    <div class="container content">
      <Notifications />
      <slot />
    </div>

    <Footer />
    <PrivacyPolicyModal :show="showPrivacyPolicyModal" />
    <TermsAndConditionsModal :show="showTermAndConditionsModal" />
    <RefreshModal />
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import NavBar from '@components/_shared/NavBar'
import Footer from '@components/_shared/Footer'
import PrivacyPolicyModal from '@components/_shared/PrivacyPolicyModal'
import TermsAndConditionsModal from '@components/_shared/TermsAndConditionsModal'
import RefreshModal from '@components/_globals/RefreshModal'
import Notifications from '@components/_globals/Notifications'
import HandoverNotificationBanner from '@views/banner/HandoverNotificationBanner'
import HandoverChannel from '@/src/utils/channel-mixin.js'

import get from 'lodash/get'
import format from 'date-fns/format'
import endOfTomorrow from 'date-fns/endOfTomorrow'
import subMinutes from 'date-fns/subMinutes'
import roundTime from '@utils/round-time'

export default {
  name: 'AppLayout',
  components: {
    NavBar,
    HandoverNotificationBanner,
    Footer,
    PrivacyPolicyModal,
    TermsAndConditionsModal,
    RefreshModal,
    Notifications,
  },
  mixins: [HandoverChannel],
  data() {
    return {
      primaryHeight: 0,
    };
  },
  computed: {
    ...mapGetters('auth', ['loggedIn', 'currentUser']),
    ...mapState('auth', ['showTerms']),
    ...mapGetters('global', ['getFeatureFlagStatus', 'getHandoverBanners', 'electronicHandoverBannerChannels']),

    isCr59Enabled() {
      return this.getFeatureFlagStatus('staff_cr59_handover_notifications')
    },

    handoverBanners() {
      return this.getHandoverBanners
    },

    hasNotifications() {
      return this.handoverBanners.length > 0;
    },

    showPrivacyPolicyModal() {
      return (
        this.loggedIn &&
        this.currentUser.privacy_accepted === false &&
        !this.showTermAndConditionsModal
      );
    },

    showTermAndConditionsModal() {
      return this.loggedIn && this.currentUser.terms_accepted === false;
    },

    canSeeHandoverNotifications() {
      return (
        get(this.currentUser, 'role.primary.dashboard_page', false) &&
        get(this.currentUser, 'role.secondary.respond_electronic_handover_action', false)
      );
    },
  },

  watch: {
    currentUser(currentUser) {
      if (currentUser && this.loggedIn) {
        if (!get(this.currentUser, 'electronic_handover_notifications', false)) {
          this.electronicHandoverBannerChannels.forEach(channel => {
            const channelObj = { channel: channel[0], station_id: channel[1] }
            const identifier = JSON.stringify(channelObj)
            this.unsubscribeFromChannel(identifier)
          })
          this.setElectronicHandoverBannerChannels([]);
          return;
        }

        this.syncHandoverSubscriptions();
      } else {
        this.setElectronicHandoverBannerChannels([]);
        this.unsubscribeFromAllChannels();
      }
    },
  },

  mounted() {
    this.loadHandoverBannersFromStorage();
  },

  beforeUnmount() {
    this.unsubscribeFromAllChannels();
    this.clearAllTimeouts();
  },

  methods: {
    ...mapActions('global', ['addHandoverBanner', 'removeHandoverBanner', 'loadHandoverBannersFromStorage', 'clearAllTimeouts', 'setElectronicHandoverBannerChannels']),

    handleHandoverNotification(data) {
      const { status, handover_type: handoverType } = data.handover
      const { id } = data.journey_leg

      if (handoverType !== 'electronic') return;

      if (['acknowledged', 'timed_out'].includes(status)) {
        this.removeHandoverBanner(id);
      } else {
        this.addHandoverBanner(id);
      }
    },

    syncHandoverSubscriptions() {
      if (!this.canSeeHandoverNotifications) return;

      // Get current user's stations
      const userStations = this.currentUser.electronic_handover_notification_stations || [];
      const newChannelNames = userStations.map(entry =>
        ['ElectronicHandoverNotificationStationChannel', entry.station.id]
      );

      // Get currently active channels
      const currentChannels = this.electronicHandoverBannerChannels;

      // Find channels to unsubscribe from (in current but not in new)
      const channelsToRemove = currentChannels.filter(currentChannel => {
        return !newChannelNames.some(newChannel =>
          newChannel[0] === currentChannel[0] && newChannel[1] === currentChannel[1]
        );
      });

      // Find channels to subscribe to (in new but not in current)
      const channelsToAdd = newChannelNames.filter(newChannel => {
        return !currentChannels.some(currentChannel =>
          currentChannel[0] === newChannel[0] && currentChannel[1] === newChannel[1]
        );
      });

      // Unsubscribe from removed channels
      channelsToRemove.forEach(channel => {
        const channelObj = { channel: channel[0], station_id: channel[1] }
        const identifier = JSON.stringify(channelObj)
        this.unsubscribeFromChannel(identifier)
      });

      // Subscribe to new channels
      if (channelsToAdd.length > 0) {
        const callbacks = {
          initialized: () => {},
          connected: () => {},
          disconnected: () => {},
          rejected: () => {},
          received: data => {
            this.handleHandoverNotification(data)
          }
        }

        channelsToAdd.forEach(channel => {
          this.subscribeToChannel(
            { channel: channel[0], station_id: channel[1] },
            callbacks
          )
        });
      }

      // Update the stored channels list
      this.setElectronicHandoverBannerChannels(newChannelNames);
    },

    handleViewDetails() {
      const tomorrowEndOfDay = subMinutes(roundTime(endOfTomorrow()), 5);

      const route = {
        name: 'dashboard-index',
        query: {
          handover_status: 'respond',
          date_to: format(tomorrowEndOfDay, 'MMM-dd-yyyy HH:mm')
        },
      };
      const url = this.$router.resolve(route).href;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
  },
};
</script>

<style lang="scss">
@import '@variables';

.banner-wrapper {
  display: grid;
}

.banner-wrapper>* {
  grid-area: 1 / 1;
}

.page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  position: relative;
}

/* MAIN PAGE CONTENT */
.container.content {
  flex: 1;
  min-height: calc(100vh - 114px);
  padding-top: 1rem;
}

/* ─── TRANSITION FOR NOTIFICATION BANNER ─── */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.4s ease;
}

.slide-fade-enter-from {
  transform: translateY(-100%);
  opacity: 0;
}

.slide-fade-enter-to {
  transform: translateY(0);
  opacity: 1;
}

.slide-fade-leave-from {
  transform: translateY(0);
  opacity: 1;
}

.slide-fade-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

.pinned {
  position: sticky;
  top: 0;
  width: 100%;
  background-color: #FAEBD7;
  color: #fff;
  z-index: 10;
}
</style>
